/**
 * LayerTotalsCaption — live per-layer weight totals + manual-balance guidance.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Each layer's group weights are set manually and must total exactly 100%. A
 * layer that's off turns warning-coloured and feeds an animated banner below.
 */

import { FC } from 'react';
import { ALL_LAYERS } from '@trafficpulse/shared';
import type { FlowLayer, FormulaGroup } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import type { PerLayerWeights } from '../../hooks';
import { groupLayers } from './per-layer-weights.utils';
import WeightTotalsWarning from './WeightTotalsWarning';
import type { WeightTotalIssue } from './weight-validation';

const LAYER_TITLE: Record<FlowLayer, string> = {
  paths: 'Paths',
  landers: 'Landers',
  offers: 'Offers',
};

export interface LayerTotalsCaptionProps {
  weights: PerLayerWeights;
  groups: ReadonlyArray<FormulaGroup>;
}

interface LayerTotal {
  layer: FlowLayer;
  total: number;
  invalid: boolean;
}

const LayerTotalsCaption: FC<LayerTotalsCaptionProps> = ({
  weights,
  groups,
}) => {
  const items: LayerTotal[] = ALL_LAYERS.filter((layer) =>
    groups.some((g) => groupLayers(g).includes(layer)),
  ).map((layer) => {
    const count = groups.filter((g) => groupLayers(g).includes(layer)).length;
    const total = sumLayer(weights[layer]);
    // 0/1 group → effectively 100; an all-zero (untouched) layer is an equal split.
    const invalid = count >= 2 && total !== 0 && total !== 100;
    return { layer, total, invalid };
  });

  if (items.length === 0) return null;

  const issues: WeightTotalIssue[] = items
    .filter((i) => i.invalid)
    .map((i) => ({ label: LAYER_TITLE[i.layer], total: i.total }));

  return (
    <div className="space-y-2">
      <p className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10.5px] text-muted-foreground">
        <span className="font-medium uppercase tracking-wide">Totals</span>
        {items.map((item) => (
          <span
            key={item.layer}
            className={cn(
              'tabular-nums transition-colors',
              item.invalid && 'font-semibold text-warning',
            )}
          >
            {LAYER_TITLE[item.layer]} {item.total}%
          </span>
        ))}
      </p>
      <WeightTotalsWarning issues={issues} />
    </div>
  );
};

function sumLayer(values: Record<string, number> | undefined): number {
  if (!values) return 0;
  return Object.values(values).reduce((sum, v) => sum + v, 0);
}

export default LayerTotalsCaption;
